/**
 * Loads Google Identity Services and drives its button from our own
 * Figma-matched `SocialButton`, rather than Google's own widget.
 *
 * Google does not let a custom element trigger its consent popup directly —
 * only a button it renders itself can. The standard workaround (and the one
 * Google's own issue tracker points integrators to) is to render Google's
 * real button into an invisible layer stacked exactly on top of the styled
 * one, so a click on the visible button lands on the real one underneath.
 * `mountInvisibleGoogleButton` is that layer.
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: {
            client_id: string
            callback: (response: { credential: string }) => void
          }): void
          renderButton(parent: HTMLElement, options: Record<string, unknown>): void
        }
      }
    }
  }
}

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve()
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

/** `VITE_GOOGLE_CLIENT_ID` unset means the button quietly stays decorative. */
export function googleClientId(): string {
  return (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? ''
}

export function googleSignInAvailable(): boolean {
  return googleClientId() !== ''
}

/**
 * Renders Google's real button into `container`, invisible and stretched to
 * fill it, so a click anywhere on the visible sibling reaches it. Returns a
 * cleanup function; safe to call from a `useEffect`.
 */
export function mountInvisibleGoogleButton(
  container: HTMLElement,
  onCredential: (idToken: string) => void,
): () => void {
  let cancelled = false
  void loadScript().then(() => {
    if (cancelled || !window.google) return
    window.google.accounts.id.initialize({
      client_id: googleClientId(),
      callback: (response) => onCredential(response.credential),
    })
    window.google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      width: container.clientWidth || 320,
    })
  })
  return () => {
    cancelled = true
  }
}
